import { DeleteIcon, EditIcon } from '@assets/icons';
import { CardButton, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { useModal } from '@components/modal';
import { CreateSecretModal, EditSecretModal, useDeleteSecretModal } from '@components/secret';
import { Typography } from '@components/typography';
import { type Secret } from '@generated/graphql';
import { type SxProps } from '@mui/material';
import { ServerTag } from '../../ServerTag';
import { ResourceCard } from '../ResourceCard';

type SecretCardProps = {
  secret: Pick<Secret, 'name' | 'secretId' | 'workspaceIds'>;
  organizationId?: string;
  workspaceId?: string;
  deleteable?: boolean;
  editable?: boolean;
  hideTag?: boolean;
  source: 'workspace' | 'organization' | 'user';
  sx?: SxProps;
  onEdit?: VoidFunction;
  onDelete?: VoidFunction;
};

export const SecretCard = ({
  secret,
  organizationId,
  workspaceId,
  deleteable = false,
  editable = false,
  hideTag = false,
  source,
  sx,
  onEdit,
  onDelete,
}: SecretCardProps) => {
  const { showModal: showEditSecretModal } = useModal({
    component: EditSecretModal,
    componentProps: { secret, organizationId, workspaceId, onEdit },
    modalProps: {
      title: `Edit ${secret?.name || 'secret'}`,
    },
  });

  const handleEditSecret = () => {
    return showEditSecretModal();
  };

  const { showModal: showDeleteSecretModal } = useDeleteSecretModal({ secret, onDelete });

  const handleDeleteSecret = () => {
    showDeleteSecretModal();
  };

  return (
    <ResourceCard sx={sx}>
      <Group
        alignItems="center"
        gap={2}
        sx={{ overflow: 'hidden', flex: '1 1 100px', minWidth: '100px' }}
      >
        <Typography variant="body1" handleOverFlow fitContent color="foreground.paperCard.default">
          {secret.name}
        </Typography>
        {!hideTag && (
          <ServerTag
            label={source}
            color={source}
            variant="caption2"
            sx={{ textTransform: 'capitalize' }}
          />
        )}
      </Group>
      <Group alignItems="center" justifyContent="flex-end" flex={1} flexShrink={0} gap={1}>
        {editable && (
          <IconButton
            Icon={EditIcon}
            tooltip="Details"
            onClick={handleEditSecret}
            size={16}
            variant="default"
          />
        )}
        {deleteable && (
          <IconButton
            Icon={DeleteIcon}
            tooltip="Delete"
            onClick={handleDeleteSecret}
            size={16}
            variant="error"
          />
        )}
      </Group>
    </ResourceCard>
  );
};

export const AddSecretCard = ({
  organizationId,
  workspaceId,
  onCreate,
}: {
  organizationId?: string;
  workspaceId?: string;
  onCreate?: VoidFunction;
}) => {
  const secretType = workspaceId ? 'workspace' : organizationId ? 'organization' : 'user';
  const { showModal } = useModal({
    component: CreateSecretModal,
    componentProps: { organizationId, workspaceId, onCreate },
    modalProps: {
      title: `Create ${secretType} secret`,
    },
  });

  const handleManageServices = () => {
    void showModal();
  };

  return (
    <CardButton
      label={`Add ${secretType} secret`}
      onClick={handleManageServices}
      background="paperCard"
      sx={{ width: '100%', minWidth: '200px', marginTop: 1 }}
    />
  );
};
